import React, { useEffect, useRef, useState } from "react"
import { addBatchFastens } from "@/reducers/messagesSlice"
import posthog from "posthog-js"
import classNames from "classnames"
import css from "../RouteView.module.css"
import { mapUpdateListener } from "./RouteView"

type DynamicScrollContainerProps = {
  children: React.ReactNode
}

const ScrollContainer = ({ children }: DynamicScrollContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [containerHeight, setContainerHeight] = useState("auto")
  const [isBlinking, setIsBlinking] = useState(false)
  const [showShadow, setShowShadow] = useState(false)

  useEffect(() => {
    const unsubscribe = mapUpdateListener.startListening({
      actionCreator: addBatchFastens,
      effect: async () => {
        setIsBlinking(true)
        setTimeout(() => setIsBlinking(false), 2000) // Reset after animation
      },
    })

    // Cleanup when component unmounts
    return () => unsubscribe()
  }, [])

  useEffect(() => {
    const currentContainer = containerRef.current
    if (!currentContainer) return

    const updateDimensions = (entries: ResizeObserverEntry[]) => {
      const entry = entries[0]
      if (entry && currentContainer.parentElement) {
        const parentHeight = currentContainer.parentElement.offsetHeight
        setContainerHeight(`${parentHeight * 0.5 - 8}px`)
      }
    }

    const resizeObserver = new ResizeObserver(updateDimensions)
    const parentElement = currentContainer.parentElement
    if (parentElement) {
      resizeObserver.observe(parentElement)
    }

    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    const content = contentRef.current
    if (!content) return

    const checkScrollPosition = () => {
      const isOverflowing = content.scrollHeight > content.clientHeight
      const isScrolledToBottom =
        content.scrollTop + content.clientHeight >= content.scrollHeight - 5
      setShowShadow(isOverflowing && !isScrolledToBottom)
    }

    // Check initially
    checkScrollPosition()

    // Check on scroll
    content.addEventListener("scroll", checkScrollPosition)

    // Check when content changes
    const resizeObserver = new ResizeObserver(checkScrollPosition)
    resizeObserver.observe(content)

    return () => {
      content.removeEventListener("scroll", checkScrollPosition)
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <div
      className={classNames(css.containerWrapper, {
        [css.blinking]: isBlinking,
      })}
      ref={containerRef}
    >
      <div
        ref={contentRef}
        className={classNames(
          "overflow-y-auto scrollbar-borderless [scrollbar-gutter:stable]",
          {
            [css.showShadow]: showShadow,
          },
        )}
        style={{ maxHeight: containerHeight }}
      >
        {children}
      </div>
    </div>
  )
}

export default ScrollContainer
